import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import SubmitFormRunner from 'teacher-ui-client/utils/submit-form-runner'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type Teacher from 'teacher-ui-client/models/teacher'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Args {
  teachers: Teacher[]
  teachersWhoCanBeDeleted: Teacher[]
  teachersWhoCantBeDeleted: Teacher[]
  subscriptionType: SubscriptionType
  closeModalAction: () => unknown
  unselectAction: () => unknown
  onSubmit: (teachers: Teacher[]) => unknown
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

interface TeacherRow {
  id: string
  fullName: string
  schoolClassNamesJoined: string
  canBeDeleted: boolean
}

interface Theme {
  alertHeaderBg: string
  headerBg: string
  rowBg: string
}

const themeClasses: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: {
    alertHeaderBg: 'bg-oceany-blue-300',
    headerBg: 'bg-oceany-blue-100',
    rowBg: 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100',
  },
  [ThemeKeys.green]: {
    alertHeaderBg: 'bg-ms-green-300',
    headerBg: 'bg-ms-green-100',
    rowBg: 'even:bg-ms-green-50 hover:bg-ms-green-100',
  },
  [ThemeKeys.darkBlue]: {
    alertHeaderBg: 'bg-wl-blue-250',
    headerBg: 'bg-wl-blue-100',
    rowBg: 'even:bg-wl-blue-50 hover:bg-wl-blue-100',
  },
  [ThemeKeys.default]: {
    alertHeaderBg: 'bg-dusty-black-300',
    headerBg: 'bg-dusty-black-100',
    rowBg: 'even:bg-dusty-black-50 hover:bg-dusty-black-100',
  },
}

export class DeleteTeachersPermanentlyForm extends Component<Signature> {
  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @tracked submitState: 'default' | 'load' | 'error' | 'done' = 'default'

  @tracked teacherRows: TeacherRow[] = []

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    // Set teacherRows here, so that what is rendered persists during and after deletion, until the form is closed
    this.teacherRows = this.args.teachers
      .filter(hasId)
      .map((teacher) => {
        const schoolClassNamesJoined = teacher.schoolClassNames.join(', ')
        const canBeDeleted = Boolean(this.args.teachersWhoCanBeDeleted.find((t) => t.id === teacher.id))
        return { id: teacher.id, fullName: teacher.fullName, schoolClassNamesJoined, canBeDeleted }
      })
      .sort((a, b) => Number(a.canBeDeleted) - Number(b.canBeDeleted))
  }

  get themeClasses() {
    return themeClasses[subscriptionTypeToThemeKey(this.args.subscriptionType)]
  }

  get deletableTeacherCount() {
    return this.teacherRows.filter((t) => t.canBeDeleted).length
  }

  get isDisabled() {
    return this.submitState === 'load' || this.submitState === 'done'
  }

  get otherSubscriptionTypes(): SubscriptionType[] {
    const subscriptionTypes = this.args.teachersWhoCantBeDeleted.reduce<SubscriptionType[]>(
      (otherSubscriptionTypes, teacher) => {
        return [...otherSubscriptionTypes, ...teacher.schoolClasses.map((item) => item.subscriptionType)]
      },
      [],
    )
    const uniqSubscriptionTypes = [...new Set(subscriptionTypes)]
    return uniqSubscriptionTypes.filter((subType) => subType !== this.args.subscriptionType)
  }

  get otherSubscriptionTypesFormatted() {
    return this.otherSubscriptionTypes.map((other) => this.intl.t(other)).join(` ${this.intl.t('or')} `)
  }

  submitSuccessful(deletedCount: number) {
    this.args.closeModalAction()
    this.args.unselectAction()
    this.flashQueue.addSuccess({
      subtitle: this.intl.t('manage.manageTeachers.bulkActions.deleteTeachers.success', { count: deletedCount }),
    })
  }

  submitFailed() {
    this.flashQueue.addFail({ subtitle: this.intl.t('manage.manageTeachers.bulkActions.deleteTeachers.fail') })
  }

  @action
  submitForm(event: Event) {
    event.preventDefault()

    const submitFormRunner = new SubmitFormRunner(this, 'submitState')
    submitFormRunner.success(() => this.submitSuccessful(this.deletableTeacherCount))
    submitFormRunner.fail(() => this.submitFailed())
    void submitFormRunner.run(this.args.onSubmit, this.args.teachersWhoCanBeDeleted)
  }

  <template>
    <div ...attributes>
      {{#if @teachersWhoCantBeDeleted}}
        <div class="px-4 pb-4 {{this.themeClasses.alertHeaderBg}}">
          <InlineAlert @type={{AlertType.Warning}} role="alert">
            <InlineAlertSubtitle>
              {{t
                "manage.manageTeachers.bulkActions.deleteTeachers.infoText"
                product=this.otherSubscriptionTypesFormatted
              }}
            </InlineAlertSubtitle>
          </InlineAlert>
        </div>
      {{/if}}

      {{#if this.deletableTeacherCount}}
        <div class="px-4 pb-4 {{this.themeClasses.alertHeaderBg}}">
          <InlineAlert @type={{AlertType.Critical}} role="alert">
            <InlineAlertSubtitle>
              {{t
                "manage.manageTeachers.bulkActions.deleteTeachers.confirm"
                count=this.deletableTeacherCount
                htmlSafe=true
              }}
            </InlineAlertSubtitle>
          </InlineAlert>
        </div>
      {{/if}}

      <form {{on "submit" this.submitForm}}>
        <div class="max-h-80 overflow-y-auto">
          <table class="w-full" tabindex="0">
            <thead>
              <tr class="text-dusty-black-300 divide-x divide-white font-bold">
                <th class="sticky top-0 px-4 py-3 {{this.themeClasses.headerBg}}">
                  {{t "manage.manageTeachers.teacherName"}}
                </th>
                <th class="sticky top-0 px-4 py-3 {{this.themeClasses.headerBg}} w-1/2">{{t "schoolClass.label"}}</th>
                <th class="sticky top-0 px-4 py-3 {{this.themeClasses.headerBg}} text-center">{{t
                    "manage.manageTeachers.bulkActions.deleteTeachers.canBeDeleted"
                  }}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.teacherRows as |teacherRow index|}}
                <tr
                  data-test-row={{index}}
                  data-test-deletable={{teacherRow.canBeDeleted}}
                  class={{this.themeClasses.rowBg}}
                >
                  <td class="px-4 py-3">{{teacherRow.fullName}}</td>
                  <td class="px-4 py-3"><div>{{teacherRow.schoolClassNamesJoined}}</div></td>
                  <td class="px-4 py-3 text-center">
                    {{#if teacherRow.canBeDeleted}}
                      <PhosphorIcon
                        data-test-teacher-deletable
                        @icon="phosphor-check-circle-bold"
                        class="text-grapey-green-300"
                      />
                    {{else}}
                      <PhosphorIcon
                        data-test-teacher-undeletable
                        @icon="phosphor-x-bold"
                        class="text-watermelony-red-300"
                      />
                    {{/if}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
        <div class="border-dusty-black-50 border-t p-2 text-right">
          <UiButton data-test-cancel disabled={{this.isDisabled}} type="reset" {{on "click" @closeModalAction}}>
            {{t "manage.manageClasses.students.bulkActions.cancel"}}
          </UiButton>
          {{#if this.deletableTeacherCount}}
            <FormWrapperSubmitButton
              data-test-submit
              @defaultText={{t
                "manage.manageTeachers.bulkActions.deleteTeachers.deleteButtonText"
                count=this.deletableTeacherCount
              }}
              @state={{this.submitState}}
              @appearance={{ButtonAppearance.red}}
            />
          {{/if}}
        </div>
      </form>
    </div>
  </template>
}

export default DeleteTeachersPermanentlyForm
